import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Star, Download, Gift, Users, DollarSign, Copy, Pencil, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const STATUS_LABEL = {
  not_requested: "Not requested",
  review_requested: "Review requested",
  collage_delivered: "Collage delivered",
  pending_verification: "Pending verification",
  verified: "Verified",
  discount_approved: "Discount approved",
  discount_applied: "Discount applied",
};

const STATUS_COLORS = {
  not_requested: "bg-zinc-100 text-zinc-700",
  review_requested: "bg-blue-100 text-blue-800",
  collage_delivered: "bg-blue-100 text-blue-800",
  pending_verification: "bg-yellow-100 text-yellow-800",
  verified: "bg-emerald-100 text-emerald-800",
  discount_approved: "bg-emerald-100 text-emerald-800",
  discount_applied: "bg-emerald-100 text-emerald-800",
};

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

/**
 * Reviews & Referrals section on the customer profile.
 * Iter43 Phase 1 — reviews list. Phase 3 — referral code, stats, reward history.
 */
export default function CustomerReviewsSection({ customerId }) {
  const [reviews, setReviews] = useState([]);
  const [refData, setRefData] = useState(null);
  const [code, setCode] = useState(null);
  const [editing, setEditing] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!customerId) return;
    try {
      const [reviewsRes, refRes, codeRes] = await Promise.all([
        api.get(`/customers/${customerId}/reviews`),
        api.get(`/customers/${customerId}/referrals`),
        api.get(`/customers/${customerId}/referral-code`),
      ]);
      setReviews(reviewsRes.data || []);
      setRefData(refRes.data || null);
      setCode(codeRes.data || null);
      setNewCode((codeRes.data || {}).code || "");
    } catch { /* silent */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, [customerId]); // eslint-disable-line

  const saveCode = async () => {
    try {
      const { data } = await api.patch(`/customers/${customerId}/referral-code`, { code: newCode.trim().toUpperCase() });
      setCode(data);
      setEditing(false);
      toast.success("Referral code updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code.code);
      toast.success("Copied");
    } catch { toast.error("Copy failed"); }
  };

  const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");
  const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const stats = refData?.stats || { referral_count: 0, completed_referrals: 0, total_revenue: 0, total_rewards_earned: 0, pending_rewards: 0, gift_cards_sent: 0 };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp" data-testid="customer-reviews-section">
      <div className="flex items-center gap-2 mb-4">
        <Star className="w-5 h-5 text-[#FF5A00]"/>
        <h2 className="font-heading text-xl font-bold">Reviews & referrals</h2>
      </div>

      {loading && <div className="text-sm text-zinc-500">Loading…</div>}

      {/* Referral code card (Iter43 Phase 3) */}
      {!loading && code && (
        <div className="mb-6 border border-zinc-100 rounded-lg p-4 bg-orange-50/40" data-testid="customer-referral-code-block">
          <div className="flex items-center gap-3 flex-wrap">
            <Gift className="w-4 h-4 text-[#FF5A00]"/>
            <div className="text-xs text-zinc-500 uppercase tracking-widest font-mono-accent">Referral code</div>
            {editing ? (
              <>
                <Input
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value)}
                  className="w-40 font-mono-accent uppercase"
                  data-testid="customer-referral-code-input"
                />
                <Button size="sm" onClick={saveCode} data-testid="customer-referral-code-save">
                  <Check className="w-3 h-3 mr-1"/> Save
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setEditing(false); setNewCode(code.code); }}>
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <div className="font-mono-accent text-xl font-bold tracking-widest" data-testid="customer-referral-code">
                  {code.code}
                </div>
                <Button size="sm" variant="outline" onClick={copyCode} data-testid="customer-referral-code-copy">
                  <Copy className="w-3 h-3"/>
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="customer-referral-code-edit">
                  <Pencil className="w-3 h-3"/>
                </Button>
              </>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-4">
            {[
              { label: "Referrals", value: stats.referral_count, testid: "referral-stat-count" },
              { label: "Completed", value: stats.completed_referrals, testid: "referral-stat-completed" },
              { label: "Revenue", value: money(stats.total_revenue), testid: "referral-stat-revenue" },
              { label: "Rewards", value: money(stats.total_rewards_earned), testid: "referral-stat-rewards" },
              { label: "Pending", value: stats.pending_rewards, testid: "referral-stat-pending" },
              { label: "Gift cards sent", value: stats.gift_cards_sent, testid: "referral-stat-sent" },
            ].map((s) => (
              <div key={s.label} className="text-center" data-testid={s.testid}>
                <div className="text-lg font-heading font-bold text-zinc-900">{s.value}</div>
                <div className="text-[10px] uppercase tracking-widest font-mono-accent text-zinc-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reviews list */}
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium">Google review requests</div>
        <span className="text-xs text-zinc-500 font-mono-accent" data-testid="customer-reviews-count">
          {reviews.length} review{reviews.length === 1 ? "" : "s"}
        </span>
      </div>

      {!loading && reviews.length === 0 && (
        <div className="text-sm text-zinc-500" data-testid="customer-reviews-empty">
          No review requests yet. Complete a job and start the post-job marketing workflow from the job page.
        </div>
      )}

      {reviews.map((r) => (
        <div
          key={r.id}
          className="border border-zinc-100 rounded-lg p-4 mb-2 flex flex-wrap items-center gap-4"
          data-testid={`customer-review-${r.id}`}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] uppercase font-mono-accent tracking-widest px-2 py-1 rounded ${STATUS_COLORS[r.status] || "bg-zinc-100 text-zinc-700"}`}>
                {STATUS_LABEL[r.status] || r.status}
              </span>
              {r.discount_pct != null && <span className="text-xs text-zinc-600">Discount {r.discount_pct}%</span>}
              {r.discount_amount != null && <span className="text-xs text-emerald-700">{money(r.discount_amount)}</span>}
            </div>
            <div className="text-xs text-zinc-500 mt-1 space-x-3">
              <span>Requested: {fmt(r.review_requested_at)}</span>
              <span>Verified: {fmt(r.verified_at)}</span>
              <span>Applied: {fmt(r.discount_applied_at)}</span>
              <span>Reminders: {r.reminder_sent_count || 0}</span>
            </div>
          </div>
          {r.collage_upload_id && (
            <a
              href={`${backend}/api/uploads/${r.collage_upload_id}/download`}
              target="_blank" rel="noreferrer"
              className="text-xs text-[#FF5A00] hover:underline flex items-center gap-1"
              data-testid={`customer-review-collage-${r.id}`}
            >
              <Download className="w-3 h-3"/> Collage
            </a>
          )}
          {r.job_id && (
            <a
              href={`/app/jobs/${r.job_id}`}
              className="text-xs text-zinc-600 hover:text-zinc-900 hover:underline"
              data-testid={`customer-review-job-${r.id}`}
            >
              Job →
            </a>
          )}
        </div>
      ))}

      {/* Rewards earned list (from referring activity) */}
      {refData && refData.rewards && refData.rewards.length > 0 && (
        <div className="mt-6" data-testid="customer-referral-rewards-list">
          <div className="text-sm font-medium mb-2 flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-emerald-600"/> Rewards earned
          </div>
          {refData.rewards.map((rw) => (
            <div
              key={rw.id}
              className="border border-zinc-100 rounded-lg p-3 mb-2 flex items-center gap-3"
              data-testid={`customer-reward-${rw.id}`}
            >
              <div className="font-mono-accent text-lg font-bold text-emerald-700">{money(rw.amount)}</div>
              <div className="text-xs text-zinc-500 flex-1">
                {fmt(rw.created_at)} · Invoice {rw.invoice_id?.slice(-6) || "—"}
              </div>
              <span className={`text-[10px] uppercase font-mono-accent tracking-widest px-2 py-1 rounded ${
                rw.status === "gift_card_sent" ? "bg-emerald-100 text-emerald-800"
                : rw.status === "approved" ? "bg-blue-100 text-blue-800"
                : "bg-yellow-100 text-yellow-800"}`}>
                {rw.status.replace(/_/g, " ")}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
